import { Card, Heading, ListRow, Separator } from '@betterment/samba-react';
import { SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';

import { RecurringTransactionsDisclosure } from './RecurringTransactionsDisclosure';

import { trackClick } from '../../../features/track';
import { PayrollContributionConfigFragment } from '../../../graphql/fragments/payrollContributionConfig.generated';
import { RecurringTransactionConfigFragment } from '../../../graphql/fragments/recurringTransactionConfig.generated';
import { PurposeRecurringTransactionConfigsQuery } from '../../../purpose/components/recurring-transactions-card/graphql/purposeRecurringTransactionConfigs.generated';
import { urlBuilder } from '../../urlBuilder';
import MessageBox from '../message-box/message-box';
import { PayrollContributionConfig } from '../payroll-contribution-config/PayrollContributionConfig';
import { PayrollContributionConfigRow } from '../payroll-contribution-config/PayrollContributionConfigRow';
import {
  RecurringTransactionsConfig,
  configFrequencyCopy,
} from '../recurring-transactions-config/RecurringTransactionsConfig';
import { RecurringTransactionsSetup } from '../recurring-transactions-config/RecurringTransactionsSetup';

type Envelope = PurposeRecurringTransactionConfigsQuery['purpose']['envelope'];
type LegalSubAccount = Extract<Envelope['legalSubAccounts'][number], { __typename: 'CashAccount' }>;
type Disclosure = LegalSubAccount['recurringDepositMotivation'];

type RecurringTransactionsCardViewProps = {
  recurringTransactionConfigs: RecurringTransactionConfigFragment[];
  isWithdrawal: (sourceLegacySubAccountId: number) => boolean;
  payrollContributionConfigs?: PayrollContributionConfigFragment[];
  recurringTransactionGuidance?: Envelope['recurringTransactionMessages'];
  disclosures?: Disclosure;
  subAccountId: string;
};

function getLegalAccountId(config: PayrollContributionConfigFragment) {
  if (config.account.__typename === 'ManagedSubAccount') {
    return config.account.legalAccount.legacyId;
  }
  return null;
}

export const RecurringTransactionsCardView = ({
  recurringTransactionConfigs,
  isWithdrawal,
  recurringTransactionGuidance = [],
  payrollContributionConfigs = [],
  disclosures,
  subAccountId,
}: RecurringTransactionsCardViewProps) => {
  const shouldShowRecurringTransactionSetup = !payrollContributionConfigs.length && !recurringTransactionConfigs.length;
  const shouldShowSavingsHeader = !!(disclosures && disclosures.shortDisclosure && disclosures.longDisclosure);
  const { enabled: isTransactionCardUpdatesEnabled } = useFeatureFlag({
    splitName: 'retail.px_recurring_transaction_card_q4_2025_enabled',
    context: 'BonusDeferrals',
  });

  const legalAccountIdValue =
    payrollContributionConfigs.length > 0 ? getLegalAccountId(payrollContributionConfigs[0]) : null;

  const deferralSettingEditUrl = legalAccountIdValue
    ? urlBuilder.appDeferralSetting.absolute({
        legalAccountId: String(legalAccountIdValue),
        return_to: window.location.pathname,
      })
    : undefined;

  return (
    <Card>
      <Card.Header>
        <Heading as="h2" variant="title200" css={{ padding: 'space.300' }}>
          {shouldShowRecurringTransactionSetup ? 'Automate your savings' : 'Recurring transactions'}
        </Heading>
      </Card.Header>
      {isTransactionCardUpdatesEnabled && <Separator css={{ fullWidthWithInset: 'space.300' }} />}

      <Card.Content>
        {isTransactionCardUpdatesEnabled && (
          <ListRow css={{ paddingTop: 'space.300', paddingBottom: 'space.0' }}>
            <ListRow.LeadingContent>
              <Heading variant="title100">401(k) contribution settings</Heading>
            </ListRow.LeadingContent>
            {deferralSettingEditUrl && (
              <ListRow.TrailingContent>
                <SambaAutoLink
                  to={deferralSettingEditUrl}
                  onClick={() => trackClick({ name: 'DeferralSettingEditIntended' })}
                >
                  Edit
                </SambaAutoLink>
              </ListRow.TrailingContent>
            )}
          </ListRow>
        )}

        {shouldShowRecurringTransactionSetup ? (
          <>
            <RecurringTransactionsSetup subAccountId={subAccountId} subText={disclosures?.description} />
            {shouldShowSavingsHeader && (
              <RecurringTransactionsDisclosure
                shortDisclosure={disclosures.shortDisclosure}
                longDisclosure={disclosures.longDisclosure}
              />
            )}
          </>
        ) : (
          <>
            {payrollContributionConfigs.length > 0 &&
              (isTransactionCardUpdatesEnabled ? (
                <>
                  {payrollContributionConfigs[0] && (
                    <PayrollContributionConfigRow
                      config={payrollContributionConfigs[0]}
                      showIcon={true}
                      showLabel={payrollContributionConfigs.length === 1}
                      css={{ paddingBottom: payrollContributionConfigs.length === 1 ? 'space.300' : 'space.0' }}
                    />
                  )}
                  {payrollContributionConfigs.length >= 2 && (
                    <PayrollContributionConfigRow
                      config={payrollContributionConfigs[1]}
                      showIcon={false}
                      showLabel={true}
                      css={{ paddingTop: 'space.0', paddingBottom: 'space.300' }}
                    />
                  )}
                </>
              ) : (
                payrollContributionConfigs.map(config => <PayrollContributionConfig key={config.id} config={config} />)
              ))}

            {isTransactionCardUpdatesEnabled && <Separator css={{ fullWidthWithInset: 'space.300' }} />}

            {recurringTransactionConfigs.length > 0 && (
              <>
                {isTransactionCardUpdatesEnabled && (
                  <ListRow css={{ paddingTop: 'space.300', paddingBottom: 'space.0' }}>
                    <ListRow.LeadingContent>
                      <Heading variant="title100">Other transactions</Heading>
                    </ListRow.LeadingContent>
                  </ListRow>
                )}
                {recurringTransactionConfigs.map(config => (
                  <RecurringTransactionsConfig
                    key={config.id}
                    config={config}
                    frequency={configFrequencyCopy(config)}
                    isWithdrawal={isWithdrawal}
                    isTransactionCardUpdatesEnabled={isTransactionCardUpdatesEnabled}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Card.Content>
      {recurringTransactionGuidance?.length > 0 && (
        <Card.Footer
          css={{
            marginX: 'space.300',
            marginBottom: 'space.300',
          }}
        >
          <MessageBox messages={recurringTransactionGuidance} location="envelope_overview_recurring_transactions" />
        </Card.Footer>
      )}
    </Card>
  );
};
